export type ScoringMode = 'performance' | 'best_practice' | 'both';

export interface ScoringModeChoice {
  id: ScoringMode;
  label: string;
}

export interface PreflightRun {
  network: string;
  campaign_metadata: Record<string, string>;
}

export interface PreflightConfig {
  scoringMode: ScoringMode;
  platforms: string[];
  // screen edits one creative on one platform at a time.
  entered: Record<string, Record<string, Record<string, string>>>;
}

export const SCORING_MODES: ScoringModeChoice[] = [
  { id: 'performance', label: 'Historical performance' },
  { id: 'best_practice', label: 'Best practice' },
  { id: 'both', label: 'Both' },
];

export function platformsLine(count: number): string {
  return `${count} platform${count === 1 ? '' : 's'} selected`;
}

export function emptyConfig(): PreflightConfig {
  return { scoringMode: 'both', platforms: [], entered: {} };
}

export function setScoringMode(config: PreflightConfig, mode: ScoringMode): PreflightConfig {
  return { ...config, scoringMode: mode };
}

export function setPlatforms(config: PreflightConfig, platforms: string[]): PreflightConfig {
  const kept = new Set(platforms);
  return {
    ...config,
    platforms,
    entered: Object.fromEntries(
      Object.entries(config.entered).map(([creativeId, byPlatform]) => [
        creativeId,
        Object.fromEntries(Object.entries(byPlatform).filter(([slug]) => kept.has(slug))),
      ]),
    ),
  };
}

export function restrictPlatforms(
  config: PreflightConfig,
  available: readonly string[],
): PreflightConfig {
  const platforms = config.platforms.filter((slug) => available.includes(slug));
  const valid =
    platforms.length === config.platforms.length &&
    Object.values(config.entered).every((byPlatform) =>
      Object.keys(byPlatform).every((slug) => platforms.includes(slug)),
    );
  return valid ? config : setPlatforms(config, platforms);
}

export function setValue(
  config: PreflightConfig,
  creativeId: string,
  platform: string,
  field: string,
  value: string,
): PreflightConfig {
  const byPlatform = config.entered[creativeId] ?? {};
  const fields = { ...(byPlatform[platform] ?? {}) };
  if (value === '') delete fields[field];
  else fields[field] = value;
  return {
    ...config,
    entered: { ...config.entered, [creativeId]: { ...byPlatform, [platform]: fields } },
  };
}

export function valuesFor(
  config: PreflightConfig,
  creativeId: string,
  platform: string,
): Record<string, string> {
  return config.entered[creativeId]?.[platform] ?? {};
}

export function applyToAll(
  config: PreflightConfig,
  creativeId: string,
  platform: string,
  creativeIds: string[],
): PreflightConfig {
  const source = valuesFor(config, creativeId, platform);
  const entered = { ...config.entered };
  for (const target of creativeIds) {
    entered[target] = { ...(entered[target] ?? {}), [platform]: { ...source } };
  }
  return { ...config, entered };
}

export function configuredRuns(config: PreflightConfig, creativeId: string): PreflightRun[] {
  return config.platforms.map((platform) => ({
    network: platform,
    campaign_metadata: valuesFor(config, creativeId, platform),
  }));
}

// Each entry is a complete sentence the run button can show as-is.
export function missingConfig(config: PreflightConfig): string[] {
  return config.platforms.length === 0 ? ['Select at least 1 platform.'] : [];
}
